/**
 * Pharmacy reports: the reconciliation checks, what customers owe, and the
 * restricted-medicine register.
 *
 * The integrity checks are the stock, payment and profit reconciliation: each
 * one is a rule that must always hold, and a failure lists the rows that break
 * it. Money figures for a period live under Finance.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { useLocationFilter, useWorkingLocation } from '../../lib/locationContext';
import { money } from '../../lib/format';
import { Badge, Card, CardHeader, ErrorState, Select, Spinner, cn } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { usePharmacy, type ListOf } from './data';
import { Table, Td } from './parts';

interface Check {
  name: string;
  description: string;
  pass: boolean;
  failures: Array<Record<string, unknown>>;
}

interface Debts {
  rows: Array<{ customerId: string | null; name: string; fromSales: string; fromOpeningBalance: string; total: string }>;
  total: string;
}

interface RegisterRow {
  id: string;
  occurred_at: string;
  branch_name: string;
  product_name: string;
  batch_number: string | null;
  direction: 'in' | 'out';
  base_quantity: number;
  balance_after: number;
  entry_kind: string;
  prescription_reference: string | null;
  patient_name: string | null;
  prescriber_name: string | null;
  recorded_by_name: string;
  witness_name: string | null;
}

export function PharmacyReportsPage(): ReactNode {
  const { business, can } = useAuth();
  const c = business?.currencyCode ?? 'NGN';
  const tz = business?.timezone ?? 'UTC';
  const location = useLocationFilter();
  const checks = usePharmacy<ListOf<Check>>('/reports/integrity');
  const debts = usePharmacy<Debts>('/reports/debts', { branchId: location.branchId });
  const passed = checks.data?.data.filter((x) => x.pass).length ?? 0;
  const total = checks.data?.data.length ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Pharmacy reports"
        description="Reconciliation of stock, payments and cost; customer debts; the restricted register"
        action={
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/pharmacy/finance" className="rounded-lg border border-slate-300 bg-white px-3 py-2 font-medium text-slate-700 hover:bg-slate-50">
              Finance &amp; profit
            </Link>
            <Link to="/reports/daily" className="rounded-lg border border-slate-300 bg-white px-3 py-2 font-medium text-slate-700 hover:bg-slate-50">
              Business reports
            </Link>
          </div>
        }
      />

      <Card>
        <CardHeader
          title="Reconciliation checks"
          subtitle="Rules that must always hold. A failure lists the records that break it."
          action={
            checks.data ? (
              <Badge tone={passed === total ? 'success' : 'danger'}>
                {passed} of {total} passing
              </Badge>
            ) : undefined
          }
        />
        {checks.isError ? (
          <ErrorState message="Could not run the checks." onRetry={() => void checks.refetch()} />
        ) : !checks.data ? (
          <Spinner label="Running checks" />
        ) : (
          <ul className="divide-y divide-slate-100">
            {checks.data.data.map((check) => (
              <CheckRow key={check.name} check={check} />
            ))}
          </ul>
        )}
      </Card>

      <Card padded={false}>
        <div className="p-4 sm:p-5">
          <CardHeader
            title="Customer debts"
            subtitle="Unpaid credit sales plus opening balances not yet paid"
            action={debts.data ? <span className="text-lg font-semibold tabular-nums">{money(debts.data.total, c)}</span> : undefined}
          />
        </div>
        {debts.isError ? (
          <div className="px-5 pb-5"><ErrorState message="Could not load debts." onRetry={() => void debts.refetch()} /></div>
        ) : !debts.data ? (
          <div className="px-5 pb-5"><Spinner /></div>
        ) : debts.data.rows.length === 0 ? (
          <p className="px-5 pb-6 text-sm text-slate-500">No customer owes anything.</p>
        ) : (
          <Table head={['Customer', 'From sales', 'Opening balance', 'Total']} minWidth={520}>
            {debts.data.rows.map((r) => (
              <tr key={r.customerId ?? 'walk-in'}>
                <Td>{r.name}</Td>
                <Td className="tabular-nums">{money(r.fromSales, c)}</Td>
                <Td className="tabular-nums">{money(r.fromOpeningBalance, c)}</Td>
                <Td className="text-right font-medium tabular-nums">{money(r.total, c)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {can('restricted:read') && <RestrictedRegister timezone={tz} />}
    </div>
  );
}

function CheckRow({ check }: { check: Check }): ReactNode {
  const [open, setOpen] = useState(false);
  const columns = check.failures[0] ? Object.keys(check.failures[0]) : [];
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-slate-900">{check.description}</p>
          <p className="text-xs text-slate-500">{check.name.replace(/_/g, ' ')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={check.pass ? 'success' : 'danger'}>{check.pass ? 'Pass' : `${check.failures.length} failing`}</Badge>
          {!check.pass && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
            >
              Rows <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="mt-3 rounded-lg border border-red-100">
          <Table head={columns} minWidth={Math.max(400, columns.length * 120)}>
            {check.failures.map((row, i) => (
              <tr key={i}>
                {columns.map((col) => (
                  <Td key={col} className="font-mono text-xs">{String(row[col] ?? '—')}</Td>
                ))}
              </tr>
            ))}
          </Table>
        </div>
      )}
    </li>
  );
}

function RestrictedRegister({ timezone }: { timezone: string }): ReactNode {
  const { locations } = useWorkingLocation();
  const [branchId, setBranchId] = useState('');
  const register = usePharmacy<ListOf<RegisterRow>>('/restricted-register', { branchId: branchId || undefined });
  const time = (iso: string) =>
    new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: timezone }).format(new Date(iso));

  return (
    <Card padded={false}>
      <div className="space-y-3 p-4 sm:p-5">
        <CardHeader
          title="Restricted-medicine register"
          subtitle="Every movement of a restricted medicine, with a running balance. Append-only."
          action={
            <div className="w-56">
              <Select
                aria-label="Location"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                placeholder="All locations"
                options={locations.map((l) => ({ value: l.id, label: l.name }))}
              />
            </div>
          }
        />
        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
          Whether this register meets the statutory requirements that apply to your premises must be confirmed by
          your superintendent pharmacist. CenoX records it; it does not certify compliance.
        </p>
      </div>
      {register.isError ? (
        <div className="px-5 pb-5"><ErrorState message="Could not load the register." onRetry={() => void register.refetch()} /></div>
      ) : !register.data ? (
        <div className="px-5 pb-5"><Spinner /></div>
      ) : register.data.data.length === 0 ? (
        <p className="px-5 pb-6 text-sm text-slate-500">No restricted medicine has moved yet.</p>
      ) : (
        <Table
          head={['Time', 'Location', 'Medicine', 'Batch', 'In / out', 'Qty', 'Balance', 'Kind', 'Prescription', 'Patient', 'Prescriber', 'Recorded by', 'Witness']}
          minWidth={1300}
        >
          {register.data.data.map((r) => (
            <tr key={r.id}>
              <Td className="whitespace-nowrap">{time(r.occurred_at)}</Td>
              <Td>{r.branch_name}</Td>
              <Td>{r.product_name}</Td>
              <Td>{r.batch_number ?? '—'}</Td>
              <Td><Badge tone={r.direction === 'in' ? 'success' : 'warning'}>{r.direction}</Badge></Td>
              <Td className="tabular-nums">{r.base_quantity}</Td>
              <Td className="font-medium tabular-nums">{r.balance_after}</Td>
              <Td>{r.entry_kind.replace(/_/g, ' ')}</Td>
              <Td>{r.prescription_reference ?? '—'}</Td>
              <Td>{r.patient_name ?? '—'}</Td>
              <Td>{r.prescriber_name ?? '—'}</Td>
              <Td>{r.recorded_by_name}</Td>
              <Td>{r.witness_name ?? '—'}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}

